/**
 * Relative ("last month", "this week", ...) date-range options shared by the
 * report date-range filters (PRO-1248).
 *
 * The string values are sent verbatim as query parameters and persisted in
 * saved views / emailed reports, so they must stay in sync with the backend
 * RelativeDateRange keys in capitan-server
 * (apps/capitan_server/date_range_filters.py).
 *
 * This module is pure TypeScript (no Angular or browser dependencies) so it may
 * be imported anywhere, including the reporting-core layer.
 */
export enum RelativeDateRange {
  today = 'today',
  yesterday = 'yesterday',
  last24Hours = 'last_24_hours',
  last7Days = 'last_7_days',
  last28Days = 'last_28_days',
  last31Days = 'last_31_days',
  last90Days = 'last_90_days',
  last365Days = 'last_365_days',
  thisWeekStartingMonday = 'this_week_starting_monday',
  thisWeekStartingSunday = 'this_week_starting_sunday',
  lastWeekStartingMonday = 'last_week_starting_monday',
  lastWeekStartingSunday = 'last_week_starting_sunday',
  thisMonth = 'this_month',
  lastMonth = 'last_month',
  thisQuarter = 'this_quarter',
  lastQuarter = 'last_quarter',
  thisYear = 'this_year',
  lastYear = 'last_year',
  yearToDate = 'year_to_date',
  next7Days = 'next_7_days',
  next30Days = 'next_30_days',
  next90Days = 'next_90_days',
  nextMonth = 'next_month',
  nextQuarter = 'next_quarter',
}

export interface RelativeDateRangeOption {
  value: RelativeDateRange;
  label: string;
}

/**
 * The relative ranges offered in the filter dropdown, in display order.
 */
export const RELATIVE_DATE_RANGE_OPTIONS: RelativeDateRangeOption[] = [
  { value: RelativeDateRange.today, label: 'Today' },
  { value: RelativeDateRange.yesterday, label: 'Yesterday' },
  { value: RelativeDateRange.last24Hours, label: 'Last 24 hours' },
  { value: RelativeDateRange.last7Days, label: 'Last 7 days' },
  { value: RelativeDateRange.last28Days, label: 'Last 28 days' },
  { value: RelativeDateRange.last31Days, label: 'Last 31 days' },
  { value: RelativeDateRange.last90Days, label: 'Last 90 days' },
  { value: RelativeDateRange.last365Days, label: 'Last 365 days' },
  { value: RelativeDateRange.thisWeekStartingMonday, label: 'This week (starting Monday)' },
  { value: RelativeDateRange.thisWeekStartingSunday, label: 'This week (starting Sunday)' },
  { value: RelativeDateRange.lastWeekStartingMonday, label: 'Last week (starting Monday)' },
  { value: RelativeDateRange.lastWeekStartingSunday, label: 'Last week (starting Sunday)' },
  { value: RelativeDateRange.thisMonth, label: 'This month' },
  { value: RelativeDateRange.lastMonth, label: 'Last month' },
  { value: RelativeDateRange.thisQuarter, label: 'This quarter' },
  { value: RelativeDateRange.lastQuarter, label: 'Last quarter' },
  { value: RelativeDateRange.thisYear, label: 'This year' },
  { value: RelativeDateRange.yearToDate, label: 'Year to date' },
  { value: RelativeDateRange.lastYear, label: 'Last year' },
];

/** Upcoming ranges; see the date-range-filter CLAUDE.md for when to offer them. */
export const FUTURE_RELATIVE_DATE_RANGE_OPTIONS: RelativeDateRangeOption[] = [
  { value: RelativeDateRange.next7Days, label: 'Next 7 days' },
  { value: RelativeDateRange.next30Days, label: 'Next 30 days' },
  { value: RelativeDateRange.next90Days, label: 'Next 90 days' },
  { value: RelativeDateRange.nextMonth, label: 'Next month' },
  { value: RelativeDateRange.nextQuarter, label: 'Next quarter' },
];

const RELATIVE_DATE_RANGE_LABELS: { [key: string]: string } = {};
for (const option of [...RELATIVE_DATE_RANGE_OPTIONS, ...FUTURE_RELATIVE_DATE_RANGE_OPTIONS]) {
  RELATIVE_DATE_RANGE_LABELS[option.value] = option.label;
}

/**
 * Human-readable label for a relative-range key (falls back to the raw key).
 */
export function getRelativeDateRangeLabel(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  return RELATIVE_DATE_RANGE_LABELS[value] || value;
}
